import React from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/shared/components/ui/button";
import { Download } from "lucide-react";
import FullscreenLayout from "@/pages/layouts/FullScreenLayout";

const TermsOfService: React.FC = () => {
  const downloadTermsOfService = () => {
    // Import jsPDF dynamically
    import("jspdf")
      .then(({ default: jsPDF }) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        const margin = 20;
        let yPosition = margin;
        const lineHeight = 6;

        // Helper function to add text with word wrapping
        const addText = (text: string, fontSize: number = 10, isBold: boolean = false) => {
          doc.setFontSize(fontSize);
          if (isBold) {
            doc.setFont("helvetica", "bold");
          } else {
            doc.setFont("helvetica", "normal");
          }

          const lines = doc.splitTextToSize(text, pageWidth - 2 * margin);

          for (const line of lines) {
            if (yPosition + lineHeight > pageHeight - margin) {
              doc.addPage();
              yPosition = margin;
            }
            doc.text(line, margin, yPosition);
            yPosition += lineHeight;
          }
          yPosition += 2; // Add some space after text
        };

        // Helper function to add a new page if needed
        const checkNewPage = () => {
          if (yPosition + 20 > pageHeight - margin) {
            doc.addPage();
            yPosition = margin;
          }
        };

        // Title
        addText("Elayne Terms of Service", 16, true);
        addText("Effective Date: January 1, 2025", 12, true);
        yPosition += 10;

        // Introduction
        addText(
          'These Terms of Service ("Terms") constitute a binding agreement between you ("you" or "User") and Ildiko, Inc., a Delaware corporation doing business as Elayne ("Elayne," "we," "us," or "our"). These Terms govern your access to and use of the Elayne platform and all related services, content, and functionality provided directly or indirectly to you as an individual consumer. By accessing or using the Elayne platform, you acknowledge that you have read, understood, and agree to be bound by these Terms, including any updates or modifications hereto.',
        );

        // Important Notice
        checkNewPage();
        addText("IMPORTANT NOTICE", 12, true);
        addText(
          "BY CLICKING 'I AGREE' (OR A SIMILAR BUTTON/CHECKBOX) AND/OR BY ACCESSING OR USING THE ELAYNE PLATFORM, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTAND, AND AGREE TO BE LEGALLY BOUND BY THESE TERMS OF SERVICE AND OUR PRIVACY POLICY. IF YOU DO NOT AGREE, DO NOT USE THE PLATFORM.",
        );

        // Section 1: Overview of Services
        addText("1. Overview of Services", 14, true);
        addText(
          "Elayne provides technology-enabled support services designed to assist individuals and their families with estate planning, digital organization, and post-loss administration. These services may include, but are not limited to: coordination and communication with financial institutions, insurance carriers, and other third parties; facilitation of administrative tasks such as account closures, beneficiary updates, and document requests; organization and digitization of estate-related information; guidance around timelines and obligations; and delivery of digital tools such as templates, checklists, and action tracking dashboards.",
        );
        addText(
          "Our services are intended to offer guidance and support, not legal or financial advice. Elayne is not a law firm, fiduciary, executor, financial advisor, or medical advisor. We do not provide legal representation or create attorney-client relationships. Any suggestions, checklists, or task tracking tools are offered solely for informational purposes and are not a substitute for professional judgment. You acknowledge that Elayne does not act as a representative or agent under any official capacity unless explicitly agreed in writing.",
        );
        addText(
          "Services are provided remotely. Elayne does not attend court appearances or perform tasks that require a physical presence, legal licensure, or other professional certification. Where appropriate, Elayne may assist you in identifying third-party professionals or service providers; however, you are solely responsible for evaluating and retaining any such providers. Elayne does not endorse or guarantee their services and disclaims all liability for their actions, advice, or omissions.",
        );

        // Section 2: Free Concierge Hours and Service Scope
        addText("2. Free Concierge Hours and Service Scope", 14, true);
        addText(
          "In special cases, User is eligible to receive up to fifteen (15) hours of personalized, one-on-one concierge services from Elayne at no charge. These services may be accessed via phone, video conferencing, email, and secure digital messaging.",
        );
        addText(
          "Elayne reserves the right to determine the scope and content of the concierge services in its sole discretion. Additional hours may be offered on a case-by-case basis, and Elayne may prioritize urgent or time-sensitive tasks over others. Elayne may decline to perform or may delay the completion of tasks that:",
        );
        addText("• require in-person delivery or filing;");
        addText("• require legal advice or professional licensure;");
        addText("• are prohibited by law, regulation, or local jurisdiction;");
        addText("• are outside the operational capabilities of Elayne's remote service model.");
        addText(
          "While we aim to provide efficient and effective support, Elayne does not guarantee the outcome of any service or task. The success of any service may depend on User cooperation, responsiveness of third parties, and timely provision of necessary information or documentation.",
        );

        // Section 3: User Eligibility, Responsibilities, and Conduct
        addText("3. User Eligibility, Responsibilities, and Conduct", 14, true);
        addText("By using the Elayne platform, you represent and warrant that:");
        addText("• you are at least eighteen (18) years of age;");
        addText("• you reside in the United States;");
        addText(
          "• you are using the platform on your own behalf or with legal authority to act for another person or estate.",
        );
        addText(
          "You are responsible for providing accurate, complete, and up-to-date information to Elayne and for maintaining the confidentiality of your account credentials. You agree to cooperate in good faith with Elayne personnel and to supply any documents or data necessary to complete requested services.",
        );
        addText("You further agree not to:");
        addText("• use the platform or services for any illegal or unauthorized purpose;");
        addText(
          "• reverse-engineer, copy, distribute, or repurpose Elayne intellectual property or content;",
        );
        addText("• interfere with or disrupt the platform or networks connected to it;");
        addText(
          "• submit content that is unlawful, defamatory, or in violation of third-party rights.",
        );

        // Section 4: Session Recording and Monitoring
        addText("4. Session Recording and Monitoring", 14, true);
        addText(
          "You understand and agree that Elayne may record audio, video, or written interactions between you and its service personnel for purposes of:",
        );
        addText("• delivering the services effectively;");
        addText("• maintaining accurate internal records;");
        addText("• training, product improvement, and internal quality assurance;");
        addText("• legal and regulatory compliance.");
        addText(
          "All recordings are securely stored, access-controlled, and not shared externally except as permitted by the User or required by law. By using the services, you consent to such recordings.",
        );

        // Section 5: Privacy, Data Security, and Confidentiality
        addText("5. Privacy, Data Security, and Confidentiality", 14, true);
        addText(
          "Elayne values your privacy. All personal, financial, and estate-related information provided by you is treated as confidential and is used solely for the purposes of delivering services or improving our offerings. We implement industry-standard security practices including encryption, secure servers, and limited employee access protocols.",
        );
        addText(
          "You acknowledge that while Elayne uses best efforts to safeguard your information, no method of transmission over the Internet or method of electronic storage is 100% secure. By using the platform, you accept this risk.",
        );
        addText(
          "Our use of your personal data is governed by our Privacy Policy, which is hereby incorporated into these Terms. You are responsible for retaining backup copies of any documents you provide to Elayne.",
        );

        // Section 6: Intellectual Property and License
        addText("6. Intellectual Property and License", 14, true);
        addText(
          "All rights, title, and interest in and to the Elayne platform, services, content, features, trademarks, and related documentation remain the exclusive property of Elayne. These Terms do not transfer any intellectual property rights to you.",
        );
        addText(
          "You are granted a non-exclusive, non-transferable, revocable license to access and use the services for your personal, non-commercial use, subject to these Terms. You may not sublicense, reproduce, modify, reverse-engineer, or distribute any portion of the platform without prior written permission from Elayne.",
        );

        // Section 7: Disclaimers
        addText("7. Disclaimers", 14, true);
        addText(
          'THE ELAYNE PLATFORM AND SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. ELAYNE EXPRESSLY DISCLAIMS ALL IMPLIED WARRANTIES INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, ACCURACY, COMPLETENESS, NON-INFRINGEMENT, OR RESULTS THAT MAY BE OBTAINED FROM USE. WE DO NOT GUARANTEE THAT THE SERVICES WILL BE UNINTERRUPTED OR ERROR-FREE OR THAT ANY INFORMATION OBTAINED WILL BE TIMELY OR ACCURATE. YOU AGREE THAT YOUR USE OF THE PLATFORM AND SERVICES IS AT YOUR OWN RISK.',
        );

        // Section 8: Limitation of Liability
        addText("8. Limitation of Liability", 14, true);
        addText(
          "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL ELAYNE, ITS OFFICERS, EMPLOYEES, AGENTS, AFFILIATES, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF USE, DATA, PROFITS, OR BUSINESS INTERRUPTION, ARISING FROM OR RELATING TO YOUR USE OF OR INABILITY TO USE THE PLATFORM OR SERVICES, EVEN IF ELAYNE HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.",
        );
        addText(
          "ELAYNE'S TOTAL CUMULATIVE LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THESE TERMS OR YOUR USE OF THE SERVICES SHALL NOT EXCEED THE GREATER OF (A) ONE HUNDRED DOLLARS ($100) OR (B) THE AMOUNT PAID BY YOU FOR THE SERVICES DURING THE TWELVE (12) MONTHS PRECEDING THE CLAIM.",
        );

        // Section 9: Indemnification
        addText("9. Indemnification", 14, true);
        addText(
          "You agree to indemnify, defend, and hold harmless Elayne and its affiliates, officers, directors, employees, and agents from and against all claims, demands, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or relating to:",
        );
        addText("• your violation of these Terms;");
        addText("• your use or misuse of the platform or services;");
        addText("• your provision of false, incomplete, or unlawful information;");
        addText("• any third-party claim resulting from your conduct.");

        // Section 10: Termination
        addText("10. Termination", 14, true);
        addText(
          "You may stop using the Elayne platform at any time. Elayne may terminate or suspend your access to the platform and services, without notice or liability, for any reason including breach of these Terms, misuse, or legal compliance.",
        );
        addText(
          "Upon termination, all rights granted to you shall cease. Sections related to confidentiality, limitations of liability, dispute resolution, and other provisions that by their nature survive termination shall remain in effect.",
        );

        // Section 11: Dispute Resolution and Arbitration
        addText("11. Dispute Resolution and Arbitration", 14, true);
        addText(
          "In the event of any dispute, claim, or controversy arising out of or relating to these Terms or the use of the services, the parties agree to resolve the dispute exclusively by final, binding, and confidential arbitration conducted under the Commercial Arbitration Rules of the American Arbitration Association. The arbitration shall take place in the State of Delaware.",
        );
        addText(
          "You waive your right to participate in any class action, collective arbitration, or representative proceeding. You may opt out of the arbitration and class action waiver provisions by notifying Elayne in writing within thirty (30) days of first accepting these Terms.",
        );

        // Section 12: Modifications and Updates
        addText("12. Modifications and Updates", 14, true);
        addText(
          "Elayne reserves the right to update, modify, or replace these Terms at any time. Changes will become effective upon posting to the platform or by other reasonable means of notification. Continued use of the services after such changes constitutes your acceptance of the revised Terms.",
        );

        // Section 13: Publicity and Testimonials
        addText("13. Publicity and Testimonials", 14, true);
        addText(
          "Elayne will never use your name, likeness, testimonial, or feedback in any marketing, promotional materials, or public-facing communications without your prior written consent. If you choose to provide a testimonial or grant us permission to share your experience, you may do so through a designated opt-in process.",
        );
        addText(
          "Elayne will not publish any personal, health-related, or estate-specific information without your explicit, documented authorization.",
        );
        addText(
          "However, Elayne reserves the right to reference anonymized anecdotes, aggregated user experiences, or generalized insights derived from service interactions, provided that such references do not identify you or disclose sensitive personal information.",
        );

        // Section 14: Miscellaneous
        addText("14. Miscellaneous", 14, true);
        addText(
          "These Terms constitute the entire agreement between you and Elayne and supersede all prior agreements regarding the services. If any provision is held to be invalid or unenforceable, the remaining provisions shall remain in full force and effect. You may not assign these Terms without Elayne's prior written consent. Elayne may freely assign these Terms in connection with a merger, sale, or corporate restructuring.",
        );
        addText(
          "Elayne shall not be held liable for any failure or delay in performance resulting from causes beyond its reasonable control, including but not limited to natural disasters, power outages, internet failures, strikes, civil unrest, or governmental actions.",
        );
        addText(
          "Any notices required under these Terms must be in writing and delivered to adria@elayne.com.",
        );

        // Closing
        addText(
          "Thank you for choosing Elayne. Our entire team is honored to support you through this process.",
          12,
          true,
        );
        addText("These Terms are accepted electronically. No physical signature is required.", 10);

        // Save the PDF
        doc.save("Elayne-Terms-of-Service.pdf");
      })
      .catch((error) => {
        console.error("Error loading jsPDF:", error);
        // Fallback to HTML download if jsPDF fails to load
        alert("Error loading PDF generator. Please try again or contact support.");
      });
  };

  return (
    <FullscreenLayout className="overflow-y-auto" childrenContainerClassName="px-4 sm:px-0 lg:px-0">
      <Helmet>
        <title>Terms of Service - Elayne</title>
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 md:py-8">
        <div className="flex items-center justify-end mb-6">
          <Button onClick={downloadTermsOfService} variant="outline">
            <Download className="h-4 w-4" />
            Download
          </Button>
        </div>

        <div>
          <h1 className="border-b-2 border-primary pb-4 mb-6">Elayne Terms of Service</h1>

          <p className="font-medium mb-6">Effective Date: January 1, 2025</p>

          <section className="mb-8">
            <p className="mb-4">
              These Terms of Service ("Terms") constitute a binding agreement between you ("you" or
              "User") and Ildiko, Inc., a Delaware corporation doing business as Elayne ("Elayne,"
              "we," "us," or "our"). These Terms govern your access to and use of the Elayne
              platform and all related services, content, and functionality provided directly or
              indirectly to you as an individual consumer. By accessing or using the Elayne
              platform, you acknowledge that you have read, understood, and agree to be bound by
              these Terms, including any updates or modifications hereto.
            </p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 my-6 font-semibold">
              BY CLICKING 'I AGREE' (OR A SIMILAR BUTTON/CHECKBOX) AND/OR BY ACCESSING OR USING THE
              ELAYNE PLATFORM, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTAND, AND AGREE TO BE
              LEGALLY BOUND BY THESE TERMS OF SERVICE AND OUR PRIVACY POLICY. IF YOU DO NOT AGREE,
              DO NOT USE THE PLATFORM.
            </div>
          </section>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">1. Overview of Services</h3>
          <p className="mb-4">
            Elayne provides technology-enabled support services designed to assist individuals and
            their families with estate planning, digital organization, and post-loss administration.
            These services may include, but are not limited to: coordination and communication with
            financial institutions, insurance carriers, and other third parties; facilitation of
            administrative tasks such as account closures, beneficiary updates, and document
            requests; organization and digitization of estate-related information; guidance around
            timelines and obligations; and delivery of digital tools such as templates, checklists,
            and action tracking dashboards.
          </p>
          <p className="mb-4">
            Our services are intended to offer guidance and support, not legal or financial advice.
            Elayne is not a law firm, fiduciary, executor, financial advisor, or medical advisor. We
            do not provide legal representation or create attorney-client relationships. Any
            suggestions, checklists, or task tracking tools are offered solely for informational
            purposes and are not a substitute for professional judgment. You acknowledge that Elayne
            does not act as a representative or agent under any official capacity unless explicitly
            agreed in writing.
          </p>
          <p className="mb-6">
            Services are provided remotely. Elayne does not attend court appearances or perform
            tasks that require a physical presence, legal licensure, or other professional
            certification. Where appropriate, Elayne may assist you in identifying third-party
            professionals or service providers; however, you are solely responsible for evaluating
            and retaining any such providers. Elayne does not endorse or guarantee their services
            and disclaims all liability for their actions, advice, or omissions.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">
            2. Free Concierge Hours and Service Scope
          </h3>
          <p className="mb-4">
            In special cases, User is eligible to receive up to fifteen (15) hours of personalized,
            one-on-one concierge services from Elayne at no charge. These services may be accessed
            via phone, video conferencing, email, and secure digital messaging.
          </p>
          <p className="mb-4">
            Elayne reserves the right to determine the scope and content of the concierge services
            in its sole discretion. Additional hours may be offered on a case-by-case basis, and
            Elayne may prioritize urgent or time-sensitive tasks over others. Elayne may decline to
            perform or may delay the completion of tasks that:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>require in-person delivery or filing;</li>
            <li>require legal advice or professional licensure;</li>
            <li>are prohibited by law, regulation, or local jurisdiction;</li>
            <li>are outside the operational capabilities of Elayne's remote service model.</li>
          </ul>
          <p className="mb-6">
            While we aim to provide efficient and effective support, Elayne does not guarantee the
            outcome of any service or task. The success of any service may depend on User
            cooperation, responsiveness of third parties, and timely provision of necessary
            information or documentation.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">
            3. User Eligibility, Responsibilities, and Conduct
          </h3>
          <p className="mb-4">By using the Elayne platform, you represent and warrant that:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>you are at least eighteen (18) years of age;</li>
            <li>you reside in the United States;</li>
            <li>
              you are using the platform on your own behalf or with legal authority to act for
              another person or estate.
            </li>
          </ul>
          <p className="mb-4">
            You are responsible for providing accurate, complete, and up-to-date information to
            Elayne and for maintaining the confidentiality of your account credentials. You agree to
            cooperate in good faith with Elayne personnel and to supply any documents or data
            necessary to complete requested services.
          </p>
          <p className="mb-4">You further agree not to:</p>
          <ul className="list-disc pl-6 mb-6 space-y-1">
            <li>use the platform or services for any illegal or unauthorized purpose;</li>
            <li>
              reverse-engineer, copy, distribute, or repurpose Elayne intellectual property or
              content;
            </li>
            <li>interfere with or disrupt the platform or networks connected to it;</li>
            <li>
              submit content that is unlawful, defamatory, or in violation of third-party rights.
            </li>
          </ul>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">
            4. Session Recording and Monitoring
          </h3>
          <p className="mb-4">
            You understand and agree that Elayne may record audio, video, or written interactions
            between you and its service personnel for purposes of:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>delivering the services effectively;</li>
            <li>maintaining accurate internal records;</li>
            <li>training, product improvement, and internal quality assurance;</li>
            <li>legal and regulatory compliance.</li>
          </ul>
          <p className="mb-6">
            All recordings are securely stored, access-controlled, and not shared externally except
            as permitted by the User or required by law. By using the services, you consent to such
            recordings.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">
            5. Privacy, Data Security, and Confidentiality
          </h3>
          <p className="mb-4">
            Elayne values your privacy. All personal, financial, and estate-related information
            provided by you is treated as confidential and is used solely for the purposes of
            delivering services or improving our offerings. We implement industry-standard security
            practices including encryption, secure servers, and limited employee access protocols.
          </p>
          <p className="mb-4">
            You acknowledge that while Elayne uses best efforts to safeguard your information, no
            method of transmission over the Internet or method of electronic storage is 100% secure.
            By using the platform, you accept this risk.
          </p>
          <p className="mb-6">
            Our use of your personal data is governed by our Privacy Policy, which is hereby
            incorporated into these Terms. You are responsible for retaining backup copies of any
            documents you provide to Elayne.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">
            6. Intellectual Property and License
          </h3>
          <p className="mb-4">
            All rights, title, and interest in and to the Elayne platform, services, content,
            features, trademarks, and related documentation remain the exclusive property of Elayne.
            These Terms do not transfer any intellectual property rights to you.
          </p>
          <p className="mb-6">
            You are granted a non-exclusive, non-transferable, revocable license to access and use
            the services for your personal, non-commercial use, subject to these Terms. You may not
            sublicense, reproduce, modify, reverse-engineer, or distribute any portion of the
            platform without prior written permission from Elayne.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">7. Disclaimers</h3>
          <p className="mb-6 font-semibold">
            THE ELAYNE PLATFORM AND SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS,
            WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. ELAYNE EXPRESSLY DISCLAIMS
            ALL IMPLIED WARRANTIES INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A
            PARTICULAR PURPOSE, TITLE, ACCURACY, COMPLETENESS, NON-INFRINGEMENT, OR RESULTS THAT MAY
            BE OBTAINED FROM USE. WE DO NOT GUARANTEE THAT THE SERVICES WILL BE UNINTERRUPTED OR
            ERROR-FREE OR THAT ANY INFORMATION OBTAINED WILL BE TIMELY OR ACCURATE. YOU AGREE THAT
            YOUR USE OF THE PLATFORM AND SERVICES IS AT YOUR OWN RISK.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">8. Limitation of Liability</h3>
          <p className="mb-4 font-semibold">
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL ELAYNE, ITS
            OFFICERS, EMPLOYEES, AGENTS, AFFILIATES, OR LICENSORS BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, INCLUDING BUT NOT
            LIMITED TO LOSS OF USE, DATA, PROFITS, OR BUSINESS INTERRUPTION, ARISING FROM OR
            RELATING TO YOUR USE OF OR INABILITY TO USE THE PLATFORM OR SERVICES, EVEN IF ELAYNE HAS
            BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p className="mb-6 font-semibold">
            ELAYNE'S TOTAL CUMULATIVE LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THESE
            TERMS OR YOUR USE OF THE SERVICES SHALL NOT EXCEED THE GREATER OF (A) ONE HUNDRED
            DOLLARS ($100) OR (B) THE AMOUNT PAID BY YOU FOR THE SERVICES DURING THE TWELVE (12)
            MONTHS PRECEDING THE CLAIM.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">9. Indemnification</h3>
          <p className="mb-4">
            You agree to indemnify, defend, and hold harmless Elayne and its affiliates, officers,
            directors, employees, and agents from and against all claims, demands, liabilities,
            damages, losses, and expenses (including reasonable attorneys' fees) arising out of or
            relating to:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-1">
            <li>your violation of these Terms;</li>
            <li>your use or misuse of the platform or services;</li>
            <li>your provision of false, incomplete, or unlawful information;</li>
            <li>any third-party claim resulting from your conduct.</li>
          </ul>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">10. Termination</h3>
          <p className="mb-4">
            You may stop using the Elayne platform at any time. Elayne may terminate or suspend your
            access to the platform and services, without notice or liability, for any reason
            including breach of these Terms, misuse, or legal compliance.
          </p>
          <p className="mb-6">
            Upon termination, all rights granted to you shall cease. Sections related to
            confidentiality, limitations of liability, dispute resolution, and other provisions that
            by their nature survive termination shall remain in effect.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">
            11. Dispute Resolution and Arbitration
          </h3>
          <p className="mb-4">
            In the event of any dispute, claim, or controversy arising out of or relating to these
            Terms or the use of the services, the parties agree to resolve the dispute exclusively
            by final, binding, and confidential arbitration conducted under the Commercial
            Arbitration Rules of the American Arbitration Association. The arbitration shall take
            place in the State of Delaware.
          </p>
          <p className="mb-6">
            You waive your right to participate in any class action, collective arbitration, or
            representative proceeding. You may opt out of the arbitration and class action waiver
            provisions by notifying Elayne in writing within thirty (30) days of first accepting
            these Terms.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">12. Modifications and Updates</h3>
          <p className="mb-6">
            Elayne reserves the right to update, modify, or replace these Terms at any time. Changes
            will become effective upon posting to the platform or by other reasonable means of
            notification. Continued use of the services after such changes constitutes your
            acceptance of the revised Terms.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">13. Publicity and Testimonials</h3>
          <p className="mb-4">
            Elayne will never use your name, likeness, testimonial, or feedback in any marketing,
            promotional materials, or public-facing communications without your prior written
            consent. If you choose to provide a testimonial or grant us permission to share your
            experience, you may do so through a designated opt-in process.
          </p>
          <p className="mb-4">
            Elayne will not publish any personal, health-related, or estate-specific information
            without your explicit, documented authorization.
          </p>
          <p className="mb-6">
            However, Elayne reserves the right to reference anonymized anecdotes, aggregated user
            experiences, or generalized insights derived from service interactions, provided that
            such references do not identify you or disclose sensitive personal information.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">14. Miscellaneous</h3>
          <p className="mb-4">
            These Terms constitute the entire agreement between you and Elayne and supersede all
            prior agreements regarding the services. If any provision is held to be invalid or
            unenforceable, the remaining provisions shall remain in full force and effect. You may
            not assign these Terms without Elayne's prior written consent. Elayne may freely assign
            these Terms in connection with a merger, sale, or corporate restructuring.
          </p>
          <p className="mb-4">
            Elayne shall not be held liable for any failure or delay in performance resulting from
            causes beyond its reasonable control, including but not limited to natural disasters,
            power outages, internet failures, strikes, civil unrest, or governmental actions.
          </p>
          <p className="mb-6">
            Any notices required under these Terms must be in writing and delivered to{" "}
            <a
              href="mailto:adria@elayne.com"
              className="text-primary underline hover:text-secondary"
            >
              adria@elayne.com
            </a>
            .
          </p>
          <div className="border-t border-primary my-4"></div>

          <p className="mb-2 font-semibold">
            Thank you for choosing Elayne. Our entire team is honored to support you through this
            process.
          </p>
          <p className="text-sm text-secondary">
            These Terms are accepted electronically. No physical signature is required.
          </p>
        </div>
      </div>
    </FullscreenLayout>
  );
};

export default TermsOfService;
